<template>
  <main inquiry>
    <div class="progress"><span :style="{ width: progressPercent + '%' }"></span></div>
    <a class="close" role="button" aria-label="닫기" @click="goContact"></a>
    <div class="scroll-inner">
      <div class="done-wrap" v-if="step === 5">
        <div class="illust" ref="illust"></div>
        <p>제출이 완료되었습니다.<br>검토 후 빠르게 연락드리겠습니다.</p>
        <button type="button" class="confirm" @click="$router.push('/')">확인</button>
      </div>
      <div class="step-wrap" v-else>
        <div class="row">
          <div class="split">
            <h1 v-if="step === 1">어떤 프로젝트를<br>의뢰하시나요?</h1>
            <h1 v-else-if="step === 2">예산과 일정은<br>어떻게 되나요?</h1>
            <h1 v-else-if="step === 3">담당자 정보와<br>의뢰 내용을 알려주세요.</h1>
            <h1 v-else-if="step === 4">의뢰 내용을<br>확인해주세요.</h1>
          </div>
          <div class="fields">
            <template v-if="step === 1">
              <div class="field">
                <label>문의 유형<span class="req">*</span></label>
                <div class="pill-group">
                  <button type="button" v-for="opt in inquiryTypes" :key="opt" :class="{ selected: form.type === opt }" @click="selectType(opt)">
                    {{ opt
                    }}
                  </button>
                </div>
              </div>
              <div class="field">
                <label>업무 범위 (중복 선택 가능)<span class="req">*</span></label>
                <div class="pill-group">
                  <button type="button" v-for="opt in scopeOptions" :key="opt" :class="{ selected: form.scope.includes(opt) }" @click="toggleScope(opt)">
                    {{ opt }}
                  </button>
                </div>
              </div>
            </template>
            <template v-else-if="step === 2">
              <div class="field">
                <label>예산<span class="req">*</span></label>
                <div class="pill-group">
                  <button type="button" v-for="opt in currentBudgetOptions" :key="opt" :class="{ selected: form.budget === opt }" @click="form.budget = opt">
                    {{ opt }}
                  </button>
                </div>
              </div>
              <div class="field">
                <label>시작 예정월<span class="req">*</span></label>
                <div class="month-field">
                  <button type="button" class="month-trigger" :class="{ selected: monthConfirmed }" @click="toggleMonthPicker">
                    {{ monthLabel }}
                    <i class="icon-calendar"></i>
                  </button>
                </div>
              </div>
              <div class="field">
                <label>{{ form.type === '연간 운영 의뢰' ? '계약 기간' : '예상 기간' }}<span class="req">*</span></label>
                <div class="pill-group">
                  <button type="button" v-for="opt in currentPeriodOptions" :key="opt" :class="{ selected: form.period === opt }" @click="form.period = opt">
                    {{ opt }}
                  </button>
                </div>
              </div>
            </template>
            <template v-else-if="step === 3">
              <div class="field-row">
                <div class="field">
                  <label>성함<span class="req">*</span></label>
                  <input type="text" v-model="form.name" placeholder="이름을 입력해주세요" @blur="touched.name = true">
                  <p class="field-error" v-if="nameError">{{ nameError }}</p>
                </div>
                <div class="field">
                  <label>회사/기업</label>
                  <input type="text" v-model="form.company" placeholder="회사명을 입력해주세요">
                </div>
              </div>
              <div class="field-row">
                <div class="field">
                  <label>이메일<span class="req">*</span></label>
                  <input type="email" v-model="form.email" placeholder="example@email.com" @blur="touched.email = true">
                  <p class="field-error" v-if="emailError">{{ emailError }}</p>
                </div>
                <div class="field">
                  <label>연락처<span class="req">*</span></label>
                  <input type="text" v-model="form.phone" placeholder="010-0000-0000" @blur="touched.phone = true">
                  <p class="field-error" v-if="phoneError">{{ phoneError }}</p>
                </div>
              </div>
              <div class="field field-message">
                <label>의뢰 내용</label>
                <textarea v-model="form.message" placeholder="요청사항을 자유롭게 작성해주세요"></textarea>
              </div>
              <div class="field">
                <input type="file" multiple ref="fileInput" class="hidden-input" @change="onFilesSelected">
                <button type="button" class="upload-btn" @click="$refs.fileInput.click()">
                  <i class="icon-upload"></i> 파일 업로드
                </button>
                <div class="file-list" v-if="form.files.length">
                <span class="chip" v-for="(f, i) in form.files" :key="i">
                  <span class="name">{{ f.name }}</span>
                  <i class="remove" @click="form.files.splice(i, 1)"></i>
                </span>
                </div>
              </div>
              <div class="agree-row">
                <input type="checkbox" id="agree" v-model="form.agree">
                <label for="agree"><a href="#" @click.prevent="showPolicy = true">개인정보 보호정책</a>에 동의합니다.</label>
              </div>
            </template>
            <template v-else-if="step === 4">
              <div class="summary">
                <div class="summary-section">
                  <div class="summary-head">
                    <h3>프로젝트 개요</h3>
                    <button type="button" class="edit-link" @click="editStep(1)">수정</button>
                  </div>
                  <div class="summary-row"><span class="k">문의 유형</span><span class="v">{{ form.type || '-' }}</span></div>
                  <div class="summary-row"><span class="k">업무 범위</span><span class="v">{{ form.scope.join(', ') || '-' }}</span></div>
                </div>
                <div class="summary-section">
                  <div class="summary-head">
                    <h3>예산 및 일정</h3>
                    <button type="button" class="edit-link" @click="editStep(2)">수정</button>
                  </div>
                  <div class="summary-row"><span class="k">예산</span><span class="v">{{ form.budget || '-' }}</span></div>
                  <div class="summary-row"><span class="k">시작 예정월</span><span class="v">{{ form.startMonth ? monthLabel : '-' }}</span></div>
                  <div class="summary-row"><span class="k">예상 기간</span><span class="v">{{ form.period || '-' }}</span></div>
                </div>
                <div class="summary-section">
                  <div class="summary-head">
                    <h3>담당자 및 의뢰 내용</h3>
                    <button type="button" class="edit-link" @click="editStep(3)">수정</button>
                  </div>
                  <div class="summary-row"><span class="k">성함</span><span class="v">{{ form.name || '-' }}</span></div>
                  <div class="summary-row"><span class="k">회사/기업</span><span class="v">{{ form.company || '-' }}</span></div>
                  <div class="summary-row"><span class="k">이메일</span><span class="v">{{ form.email || '-' }}</span></div>
                  <div class="summary-row"><span class="k">연락처</span><span class="v">{{ form.phone || '-' }}</span></div>
                  <div class="summary-row stacked"><span class="k">의뢰 내용</span><span class="v">{{ form.message || '-' }}</span></div>
                  <div class="summary-row stacked" v-if="form.files.length">
                    <span class="k">첨부 파일</span>
                    <div class="v file-list-review">
                      <div class="file-row-value" v-for="(f, i) in form.files" :key="i">
                        <span class="fname">{{ f.name }}</span><span class="fsize">{{ formatFileSize(f.size) }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </template>
          </div>
        </div>
        <p class="submit-error" v-if="submitError">{{ submitError }}</p>
      </div>
    </div>
    <div class="nav" v-if="step < 5">
      <div class="nav-inner">
        <button type="button" class="prev" v-if="step > 1" @click="prevStep">이전</button>
        <button type="button" class="next" :disabled="!canProceed || submitting" @click="nextStep">
          <template v-if="step === 4">{{ submitting ? '제출 중...' : '의뢰하기' }}<i class="icon-arrow-submit" v-if="!submitting"></i></template>
          <template v-else-if="step === 3">완료</template>
          <template v-else>다음</template>
        </button>
      </div>
    </div>

    <div class="month-picker-overlay" v-if="showMonthPicker" @click.self="showMonthPicker = false">
      <div class="picker-pop">
        <div class="picker-head">
          <button type="button" class="year-prev" :disabled="isPrevYearDisabled" @click="pickerYear--"><i class="icon-arrow"></i></button>
          <strong>{{ pickerYear }}</strong>
          <button type="button" class="year-next" @click="pickerYear++"><i class="icon-arrow"></i></button>
        </div>
        <div class="picker-grid">
          <button type="button" v-for="m in 12" :key="m" :disabled="isMonthDisabled(pickerYear, m)" :class="{ selected: tempYear === pickerYear && tempMonth === m }" @click="tempYear = pickerYear; tempMonth = m">
            {{ m }}
          </button>
        </div>
        <button type="button" class="apply" @click="applyMonth">적용</button>
      </div>
    </div>

    <div class="policy-overlay" v-if="showPolicy" @click.self="showPolicy = false">
      <div class="policy-modal">
        <h2>개인정보 보호정책</h2>
        <div class="policy-body">
          <p>뉴타입이미지웍스는 프로젝트 문의 및 상담을 위해 필요한 최소한의 개인정보를 수집·이용합니다. 수집된 개인정보는 아래의 목적에 한하여 이용되며, 관련 법령에 따라 안전하게 관리됩니다.</p>
          <h3>1. 개인정보의 수집·이용 목적</h3>
          <p>프로젝트 문의 접수 및 상담, 의뢰 내용 확인, 견적 및 일정 안내, 문의에 대한 회신 및 원활한 커뮤니케이션을 위해 개인정보를 수집·이용합니다.</p>
          <h3>2. 수집하는 개인정보 항목</h3>
          <p>필수 항목: 성명, 이메일, 연락처, 예산, 예상 일정<br>선택 항목: 회사/기업명, 첨부파일, 프로젝트 상세 내용 및 기타 문의사항</p>
          <h3>3. 개인정보의 보유 및 이용 기간</h3>
          <p>수집된 개인정보는 프로젝트 문의 및 상담 목적이 달성된 후 지체 없이 파기합니다. 다만, 관련 법령에 따라 개인정보를 보존할 필요가 있는 경우에는 해당 법령에서 정한 기간 동안 보관할 수 있습니다.</p>
          <h3>4. 개인정보 수집·이용 동의 거부에 관한 사항</h3>
          <p>개인정보 수집·이용에 대한 동의를 거부할 권리가 있습니다. 다만, 필수 항목에 대한 개인정보 수집·이용에 동의하지 않을 경우 프로젝트 문의 접수 및 상담이 제한될 수 있습니다. 선택 항목은 동의하지 않더라도 프로젝트 문의가 가능합니다.</p>
        </div>
        <div class="confirm-warp">
          <button type="button" class="confirm" @click="showPolicy = false">확인</button>
        </div>
      </div>
    </div>
  </main>
</template>

<script>
import oax from '@/utils/oax';

export default {
  name: 'inquiry',
  data() {
    return {
      step: 1,
      totalSteps: 5,
      editingReturn: false,
      inquiryTypes: ['프로젝트 의뢰', '연간 운영 의뢰'],
      scopeOptions: ['Branding', 'UX', 'Video & Motion', '기타'],
      projectBudgetOptions: ['5천 미만', '5천 ~ 1억', '1억 ~ 5억', '5억 ~ 10억', '10억 이상'],
      opsBudgetOptions: ['1억 미만', '1억 ~ 5억', '5억 ~ 10억', '10억 이상'],
      projectPeriodOptions: ['3개월 미만', '3~6개월', '6개월 이상', '조율 가능'],
      opsPeriodOptions: ['1년 미만', '1년 이상', '조율 가능'],
      showMonthPicker: false,
      monthConfirmed: false,
      showPolicy: false,
      submitting: false,
      submitError: null,
      lottieAnim: null,
      touched: { name: false, email: false, phone: false },
      pickerYear: this.nextMonthDate().getFullYear(),
      tempYear: this.nextMonthDate().getFullYear(),
      tempMonth: this.nextMonthDate().getMonth() + 1,
      form: {
        type: null,
        scope: [],
        budget: null,
        startMonth: this.formatMonth(this.nextMonthDate()),
        period: null,
        name: '',
        company: '',
        email: '',
        phone: '',
        message: '',
        agree: false,
        files: [],
      },
    };
  },
  computed: {
    progressPercent() {
      return (this.step / this.totalSteps) * 100;
    },
    monthLabel() {
      if (!this.form.startMonth) return '선택해주세요';
      const [y, m] = this.form.startMonth.split('-');
      return `${ y }년 ${ parseInt(m, 10) }월`;
    },
    currentBudgetOptions() {
      return this.form.type === '연간 운영 의뢰' ? this.opsBudgetOptions : this.projectBudgetOptions;
    },
    currentPeriodOptions() {
      return this.form.type === '연간 운영 의뢰' ? this.opsPeriodOptions : this.projectPeriodOptions;
    },
    canProceed() {
      if (this.step === 1) {
        return !!this.form.type && this.form.scope.length > 0;
      }
      if (this.step === 2) {
        const periodOk = !!this.form.period;
        return !!this.form.budget && !!this.form.startMonth && periodOk;
      }
      if (this.step === 3) {
        return !!this.form.name.trim() && this.isValidEmail(this.form.email) && this.isValidPhone(this.form.phone) && this.form.agree;
      }
      if (this.step === 4) {
        return true;
      }
      return false;
    },
    isPrevYearDisabled() {
      return this.pickerYear <= new Date().getFullYear();
    },
    nameError() {
      if (!this.touched.name || this.form.name.trim()) return null;
      return '이름을 입력해 주세요.';
    },
    emailError() {
      if (!this.touched.email) return null;
      const v = this.form.email.trim();
      if (!v) return '이메일을 입력해 주세요.';
      return this.isValidEmail(v) ? null : '올바른 이메일 주소를 입력해 주세요.';
    },
    phoneError() {
      if (!this.touched.phone) return null;
      const v = this.form.phone.trim();
      if (!v) return '연락처를 입력해 주세요.';
      if (/[^0-9-]/.test(v)) return '숫자로만 입력해 주세요.';
      return this.isValidPhone(v) ? null : '올바른 연락처를 입력해 주세요.';
    },
  },
  methods: {
    nextMonthDate() {
      const d = new Date();
      d.setDate(1);
      d.setMonth(d.getMonth() + 1);
      return d;
    },
    formatMonth(date) {
      return `${ date.getFullYear() }-${ String(date.getMonth() + 1).padStart(2, '0') }-01`;
    },
    selectType(opt) {
      if (this.form.type === opt) return;
      this.form.type = opt;
      this.form.budget = null;
      this.form.period = null;
    },
    toggleScope(opt) {
      const idx = this.form.scope.indexOf(opt);
      if (idx > -1) this.form.scope.splice(idx, 1);
      else this.form.scope.push(opt);
    },
    toggleMonthPicker() {
      if (!this.showMonthPicker && this.form.startMonth) {
        const [y, m] = this.form.startMonth.split('-');
        this.pickerYear = parseInt(y, 10);
        this.tempYear = parseInt(y, 10);
        this.tempMonth = parseInt(m, 10);
      }
      this.showMonthPicker = !this.showMonthPicker;
    },
    isMonthDisabled(year, month) {
      const now = new Date();
      const curYear = now.getFullYear();
      const curMonth = now.getMonth() + 1;
      return year < curYear || (year === curYear && month < curMonth);
    },
    applyMonth() {
      this.form.startMonth = `${ this.tempYear }-${ String(this.tempMonth).padStart(2, '0') }-01`;
      this.monthConfirmed = true;
      this.showMonthPicker = false;
    },
    isValidEmail(v) {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((v || '').trim());
    },
    isValidPhone(v) {
      return /^01[0-9]-?[0-9]{3,4}-?[0-9]{4}$/.test((v || '').trim());
    },
    onFilesSelected(e) {
      this.form.files.push(...e.target.files);
      e.target.value = '';
    },
    formatFileSize(bytes) {
      const kb = bytes / 1024;
      if (kb >= 1024) return `${ (kb / 1024).toFixed(1) } MB`;
      return `${ Math.max(1, Math.round(kb)) } KB`;
    },
    nextStep() {
      if (!this.canProceed) return;
      if (this.editingReturn) {
        this.editingReturn = false;
        this.step = 4;
        return;
      }
      if (this.step === 4) {
        this.submit();
        return;
      }
      this.step += 1;
    },
    prevStep() {
      this.step -= 1;
    },
    goContact() {
      this.localeLink('/works/article/contact');
    },
    editStep(n) {
      this.editingReturn = true;
      this.step = n;
    },
    async submit() {
      this.submitting = true;
      this.submitError = null;
      try {
        await oax.multipart('/api/inquiry', {
          type: this.form.type,
          scope: this.form.scope.join(','),
          budget: this.form.budget,
          startMonth: this.form.startMonth,
          period: this.form.period,
          name: this.form.name,
          company: this.form.company,
          email: this.form.email,
          phone: this.form.phone,
          message: this.form.message,
          agree: this.form.agree,
          attachment: { toString: () => 'FILE_INPUT_MODEL', add: this.form.files },
        });
        this.step = 5;
      } catch (e) {
        this.submitError = e?.response?.data?.message || '제출 중 오류가 발생했습니다. 잠시 후 다시 시도해주세요.';
      } finally {
        this.submitting = false;
      }
    },
    async initLottie() {
      if (this.lottieAnim || !this.$refs.illust) return;
      const lottie = (await import('lottie-web')).default;
      this.lottieAnim = lottie.loadAnimation({
        container: this.$refs.illust,
        renderer: 'svg',
        loop: true,
        autoplay: true,
        path: '/img/inquiry/letter-walk.json',
      });
    },
  },
  watch: {
    step(v) {
      if (v === 5) this.$nextTick(this.initLottie);
    },
  },
  mounted() {
    this.$store.commit('theme', 'default');
    document.body.style.backgroundColor = '#F5F5F5';
    if (this.step === 5) this.$nextTick(this.initLottie);
  },
  beforeDestroy() {
    document.body.style.backgroundColor = '';
    if (this.lottieAnim) this.lottieAnim.destroy();
  },
};
</script>

<style lang="less">
@import '~@/less/proj.less';

[inquiry] { .rel; .fvh(); overflow: hidden; .-box; .bgc(#F5F5F5);
  .progress { .abs; .lt(0, 0); .w(100%); .h(3); .bgc(transparent); .z(10);
    span { .block; .h(100%); .bgc(#111); transition: width .3s ease; }
  }
  .close { .abs; .rt(15, 15); .wh(52); .no-repeat('/img/common/ico-close-b.svg'); .bg-s(32, 32); .bg-c; .z(20); .pointer;
    &:before { .cnt; .abs; .lt; .f; .br(50%); .bgc(rgba(0, 0, 0, 0.08)); .o(0); transition: opacity .3s ease; }
    .no-touch &:hover:before { .o(1); }
  }
  .scroll-inner { .h(100%); overflow-y: auto; -webkit-overflow-scrolling: touch; }
  .step-wrap { .pt(110); .pb(180); .space-m; }
  .row { .block; }
  .split { .mb(60);
    h1 { .fs(26, 36); .bold; }
  }
  .fields { .flex-column; align-items: stretch; }
  .field { .mb(40); .rel;
    label { .block; .fs(14, 1.2); .medium; .c(#444); .mb(8); }
  }
  .field-message { .mb(20); }
  .pill-group { .flex-wrap;
    button { .ib; .gap-hr(8); .br(999); .p(13, 20); .fs(15, 1.2); font-family: inherit; .c(#333); .bgc(#E8E8E8); border: 0; .pointer; .mb(8);
      &:hover { .bgc(#E0E0E0); }
      &.selected { .bgc(#111); .c(#fff);
        &:hover { .bgc(#222); }
      }
    }
  }
  .month-field { .max-w(156);
    .month-trigger { .flex; .space-between; .align-center; .w(100%); .br(999); .p(13, 20); .fs(15, 1.2); .regular; font-family: inherit; .bgc(#E8E8E8); .c(#333); border: 0; .pointer;
      &.selected { .bgc(#111); .c(#fff);
        .icon-calendar { filter: invert(1); }
      }
    }
    .icon-calendar { .ib; .wh(20, 20); .contain('/img/common/ic-calendar.svg'); }
  }
  .month-picker-overlay { .abs; .lt(0, 0); .f; .-box; .bgc(rgba(0, 0, 0, .5)); .z(100); .flex-center; .p(20);
    .picker-pop { .w(280); .max-w(100%); .-box; .bgc(#fff); .br(20); .p(24); box-shadow: 0 10px 40px rgba(0, 0, 0, .12); }
    .picker-head { .flex; .align-center; .justify-center; .mb(20);
      button { .bgc(transparent); border: 0; .p(0); .pointer; .fs(0);
        &:disabled { .o(.3); cursor: default; }
      }
      .icon-arrow { .ib; .wh(20, 20); .contain('/img/common/ic-arrow-right.svg'); }
      .year-prev { .mr(20); }
      .year-prev .icon-arrow { transform: scaleX(-1); }
      .year-next { .ml(20); }
      strong { .fs(22, 1); .bold; }
    }
    .picker-grid { .grid(4, 10); .mb(20); justify-items: center;
      button { .bgc(transparent); border: 0; .fs(14, 32); .wh(32, 32); .-box; .br(999); .c(#111); .pointer;
        &:disabled { .c(#ccc); cursor: default; }
        &.selected { .bgc(#111); .c(#fff); }
      }
    }
    .apply { .block; .mh-c; .bgc(#111); .c(#fff); .br(999); .p(10, 24); .fs(14, 20); .bold; border: 0; .pointer; }
  }
  .field-row { .flex-column;
    .field { .w(100%); }
  }
  input[type='text'], input[type='email'], textarea { .w(100%); .-box; .br(2); .p(13, 16); .fs(16, 20); .regular; .bgc(#EEEEEE); border: 0; .-b(#EEEEEE); font-family: inherit; .c(#555); transition: background-color .2s ease;
    &::placeholder { .c(#999); }
    &:focus, &:hover { .bgc(#e0e0e0); .c(#1c1c1c); outline: 0; box-shadow: none; }
  }
  textarea { .h(206); resize: vertical; .regular; line-height: 1.6; }
  .field-error { .abs; .lt(0, 100%); .w(100%); .c(#e5484d); .fs(13, 20); .mt(8); }
  .hidden-input { .hide; }
  .upload-btn { .inline-flex; .align-center; .justify-center; .gap-hr(6); .br(999); .-box; .h(42); .p(0, 18); .fs(14, 20); .bold; .bgc(#EAEAEA); .c(#444); border: 0; .pointer;
    &:hover { .bgc(#E0E0E0); }
  }
  .icon-upload { .ib; .wh(18, 18); .mr(4); .contain('/img/common/ic-upload.svg'); }
  .file-list { .mt(12); .flex-wrap;
    .chip { .flex; .align-center; .space-between; .-box; .br(4); .p(8, 12); .fs(15, 18); .bgc(#eee); .c(#888); .mb(8); .mr(8); .max-w(240);
      .name { .ellipsis; }
      .remove { .ib; .wh(14, 14); .ml(12); .pointer; .contain('/img/common/ic-close-small.svg'); flex-shrink: 0; }
    }
  }
  .agree-row { .flex; .align-center; .mt(4);
    input[type='checkbox'] { .wh(16, 16); .mr(10); .-box; .br(4); .pointer; flex-shrink: 0;
      appearance: none; -webkit-appearance: none;
      .bgc(#fff); .-a(#8f9ba6);
      &:checked { .bgc(#111); .-a(#111);
        background-image: url('data:image/svg+xml,%3csvg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 8 8\'%3e%3cpath fill=\'%23fff\' d=\'M6.564.75l-3.59 3.612-1.538-1.55L0 4.26 2.974 7.25 8 2.193z\'/%3e%3c/svg%3e');
        background-repeat: no-repeat; background-position: center; background-size: 65%;
      }
      &:focus { outline: 0; box-shadow: 0 0 0 .2rem rgba(32, 168, 216, .25); }
    }
    label { .fs(14, 20); .c(#444444);
      a { .c(#111); .underline; }
    }
  }
  .summary { .flex-column; align-items: stretch;
    .summary-section { .gap-v(50); }
    .summary-head { .flex; .space-between; .align-center; .-b(#444); .pb(12); .mb(4);
      h3 { .fs(17, 1.2); .semi-bold; .c(#111); }
    }
    .edit-link { .bgc(#EBEBEB); .br(999); .p(7, 14); .fs(14, 1.2); .c(#444); border: 0; .pointer; }
    .summary-row { .flex; .align-start; .pv(16);
      &:not(:last-child) { .-b(#E9E9E9); }
      .k { .w(100); flex-shrink: 0; .fs(15, 1.4); .c(#888); }
      .v { flex: 1; .min-w(0); .fs(15, 1.4); .c(#333); white-space: pre-line; }
      &.stacked { flex-direction: column; align-items: flex-start;
        .k { .w(auto); .mb(16); }
        .v { .w(100%); }
      }
      .file-list-review { .flex-column; align-items: stretch; }
      .file-row-value { .flex; .space-between; .align-center; .w(100%); .gap-v(10);
        .fname { .ellipsis; }
        .fsize { flex-shrink: 0; .c(#888); }
      }
    }
  }
  .submit-error { .space-m; .c(#e5484d); .fs(13, 20); .bold; .tc; .mt(-20); .mb(20); }
  .done-wrap { .h(100%); .-box; .flex-column; .align-center; .tc; .pt(200); .pb(110); .space-m;
    .illust { .ib; .wh(180, 105); .mb(30); }
    p { .fs(18, 1.3); .semi-bold; }
    .confirm { .mt(24); .ib; .bgc(#111); .c(#fff); .br(999); .p(16, 28); .fs(15, 1); .bold; border: 0; .pointer;
      &:hover { .bgc(#222); }
    }
  }
  .policy-overlay { .abs; .lt(0, 0); .f; .-box; .bgc(rgba(0, 0, 0, .5)); .z(100); .flex-center; .p(20);
    .policy-modal { .bgc(#fff); .br(24); .pv(32); .ph(20); .-box; .max-w(600); .w(100%); .h(80vh); display: flex; flex-flow: column;
      h2 { .fs(18, 28); .bold; .mb(32); flex-shrink: 0; }
      h3 { .fs(16, 1.6); .semi-bold; .mt(25); }
      p { .fs(16, 1.6); .c(#666); }
      .policy-body { flex: 1; min-height: 0; overflow-y: auto; }
      .confirm-warp { .tc; flex-shrink: 0; }
      .confirm { .mt(28); .ib; .bgc(#111); .c(#fff); .br(999); .p(16, 28); .fs(15, 1); .bold; border: 0; .pointer; }
    }
  }
  .nav { .abs; .lb(0, 0); .w(100%); .h(110); .z(15); .-box; background: linear-gradient(to bottom, rgba(255, 255, 255, 0) 0%, #F5F5F5 15%); .pv(30);
    .nav-inner { .space-m; .flex; .align-center; .space-between; }
    button { .pointer; .br(999); .fs(15, 20); .bold; border: 0;
      &.prev { .-box; .bgc(transparent); .c(#666); .-a(#dcdcdc); .p(14, 24);
        &:hover { .c(#222); }
      }
      &.next { .inline-flex; .align-center; .gap-hr(8); .bgc(#111); .c(#fff); .p(15, 28); margin-left: auto;
        &:hover:not(:disabled) { .bgc(#222); }
        &:disabled { .bgc(#999); .c(#fff); cursor: default; }
      }
    }
    .icon-arrow-submit { .ib; .wh(14, 14); .ml(7); .contain('/img/common/ic-arrow-submit.svg'); }
  }
}

@media (min-width: @screen-tp-min) {
  [inquiry] {
    .step-wrap { .pt(140); .ph(0); .w(608); .mh-c; }
    .split {
      h1 { .fs(32, 1.4); }
    }
    .fields { flex: 1; .min-w(0); }
    .field { .mb(50);
      label { .fs(16, 1.2); .mb(10); }
    }
    .field-message { .mb(20); }
    .field-row { display: flex; flex-direction: row; align-items: flex-start;
      .field { flex: 1; .gap-h(20); }
    }
    .nav .nav-inner { .ph(0); .w(608); .mh-c; }
    .pill-group button { .fs(16, 1.4); }
    .month-field .month-trigger { .fs(16, 1.4); }
    .upload-btn { .h(48); .fs(16, 20); }
    .icon-upload { .wh(20, 20); }
    textarea { .h(224); }
    .agree-row label { .fs(16, 20); }
    .done-wrap { .pt(280);
      .illust { .wh(260, 151.667); }
      p { .fs(24, 1.3); }
    }
    .policy-overlay .policy-modal { .ph(32);
      h2 { .fs(22, 28); }
    }
    .summary {
      .summary-section { .gap-v(60); }
      .summary-head h3 { .fs(18, 1.2); }
      .summary-row {
        .k, .v { .fs(15, 1.4); }
        .k { .w(120); }
      }
      .edit-link { .fs(16, 1.2);
        &:hover { .bgc(#DCDCDC); }
      }
      .summary-row.stacked { flex-direction: row; align-items: flex-start;
        .k { .w(120); .mb(0); }
        .v { .w(auto); flex: 1; }
      }
    }
  }
}

@media (min-width: @screen-dm-min) {
  [inquiry] {
    .step-wrap { .pt(160); .w(1100); }
    .row { .flex; }
    .split { .w(500); .mb(0); flex-shrink: 0;
      h1 { .fs(36, 1.4); }
    }
    .fields { flex: 1; .min-w(0); }
    .field-row { display: flex; flex-direction: row; align-items: flex-start;
      .field { flex: 1; .gap-h(20); }
    }
    .nav .nav-inner { .w(1100); }
    .done-wrap { .pt(200);
      .illust { .wh(288, 168); }
      p { .fs(28, 1.3); }
    }
    .policy-overlay .policy-modal h2 { .fs(24, 28); }
    .summary {
      .summary-head h3 { .fs(20, 1.2); }
      .summary-row { .pv(20);
        .k, .v { .fs(16, 1.4); }
      }
    }
  }
}
</style>
